import React from "react";
import { Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import { useProSignup } from "@/components/ProfessionalSignupModal";
import {
  Briefcase,
  Heart,
  Compass,
  Sparkle,
  ArrowRight,
  CurrencyCircleDollar,
  TrendUp,
  Megaphone,
  ShieldCheck,
} from "@phosphor-icons/react";

const ROLES = [
  {
    icon: Heart,
    title: "Mental Health Professional",
    desc: "We're building a deliberately diverse bench, counselling clinical, neuropsychological, and beyond.",
    tag: "Therapy",
  },
  {
    icon: Compass,
    title: "Psychology Career & Study-Abroad Mentor",
    desc: "College, job, applications, career counselling to students & parents",
    tag: "Forward",
  },
  {
    icon: Sparkle,
    title: "Cognitive Performance Coach",
    desc: "Brain-fog, focus rebuilding, executive function.",
    tag: "Couch",
  },
  {
    icon: Briefcase,
    title: "Workshop Facilitator",
    desc: "Schools, universities, corporates, organisational wellbeing.",
    tag: "Workshops",
  },
];

const PERKS = [
  { icon: CurrencyCircleDollar, title: "Transparent payouts", body: "Monthly settlement. Razorpay-powered. Clean statements you can audit anytime." },
  { icon: TrendUp, title: "Continued Growth", body: "Free access to everything MindDhara builds, Forward mentorship, workshops, ATLAS research, for your own development." },
  { icon: Megaphone, title: "Vetted clients", body: "Intent-checked, brief-matched clients, no cold outreach, no client-acquisition costs." },
  { icon: ShieldCheck, title: "Clinical governance", body: "Supervision pathway, peer review and a code of practice you can stand behind." },
];

export default function Career() {
  const { open: openProSignup } = useProSignup();

  return (
    <Page>
      <Seo
        title="Career at MindDhara · Join as a Therapist, Mentor or Coach"
        description="Apply to join MindDhara as a clinical psychologist, mentor or coach. Vetted clients, transparent payouts and full admin support."
      />

      {/* HERO */}
      <section className="md-section md-career-hero" data-testid="career-hero">
        <div className="md-container">
          <div className="md-career-hero-grid">
            <div>
              <div className="md-label-eyebrow" style={{ color: "var(--md-secondary)" }}>Careers at <span className="md-brand">MindDhara</span></div>
              <h1 className="text-4xl md:text-6xl font-serif mt-3" data-testid="career-hero-title">
                Why work at <span style={{ color: "var(--md-secondary)" }}>MindDhara</span>?
              </h1>
              <p className="text-base md:text-lg mt-5" style={{ color: "rgba(255,255,255,0.84)", maxWidth: 560 }}>
                Our values are Cultural, Co-production, Evidence &amp; Innovation, Dignity, our promise is that they apply equally to the people who provide support and the people who receive it.
              </p>
              <p className="text-base md:text-lg mt-4" style={{ color: "rgba(255,255,255,0.84)", maxWidth: 560 }}>
                Curious about how we practice it? Apply below or contact us.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={openProSignup} className="btn-primary md-career-apply-btn" data-testid="career-apply-cta">
                  Apply to join <ArrowRight size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById("career-benefits")?.scrollIntoView({ behavior: "smooth" })}
                  className="btn-outline md-career-outline-light"
                  data-testid="career-see-benefits"
                >
                  See the Benefits
                </button>
              </div>
            </div>
            <aside className="md-career-hero-aside" data-testid="career-hero-aside">
              <div className="md-label-eyebrow" style={{ color: "rgba(255,255,255,0.72)" }}>Our Network</div>
              <h3 className="text-xl font-serif mt-2" style={{ color: "var(--md-bg)" }}>Referring providers</h3>
              <p className="text-sm mt-2.5" style={{ color: "rgba(255,255,255,0.78)" }}>
                We&apos;re building South Asia&apos;s mental wellness ecosystem, from clinical wellbeing to psychology career mentorship to culturally informed research.
              </p>
              <p className="text-sm mt-2.5" style={{ color: "rgba(255,255,255,0.78)" }}>
                Want to be at the forefront of making a meaningful change in people&apos;s lives by supporting mind care and brain health?
              </p>
              <Link to="/contact?type=referral" className="btn-accent text-sm mt-5 inline-flex" data-testid="career-refer-btn">
                Refer to us <ArrowRight size={14} weight="bold" />
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="md-section" data-testid="career-roles">
        <div className="md-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="md-label-eyebrow mb-3">Open Roles</div>
            <h2 className="text-3xl md:text-5xl font-serif">Four ways to practise with us</h2>
            <p className="text-base mt-3" style={{ color: "var(--md-text-soft)" }}>
              Apply to any role below, our team reviews and gets back within a week.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {ROLES.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.title}
                  type="button"
                  onClick={openProSignup}
                  className="md-career-role-card"
                  data-testid={`career-role-${r.tag.toLowerCase()}`}
                >
                  <div className="md-career-role-ico"><Icon size={28} weight="duotone" /></div>
                  <div className="md-career-role-body">
                    <div className="md-career-role-tag">{r.tag}</div>
                    <h3 className="font-serif text-xl mt-1">{r.title}</h3>
                    <p className="text-sm mt-2" style={{ color: "var(--md-text-soft)" }}>{r.desc}</p>
                  </div>
                  <ArrowRight size={18} weight="bold" className="md-career-role-arrow" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* PERKS / BENEFITS */}
      <section id="career-benefits" className="md-section" style={{ background: "var(--md-bg-alt)" }} data-testid="career-perks">
        <div className="md-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="md-label-eyebrow mb-3">Your Benefits</div>
            <h2 className="text-3xl md:text-5xl font-serif">A platform built around the practitioner</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="md-career-perk-card" data-testid={`career-perk-${p.title.toLowerCase().split(" ")[0]}`}>
                  <Icon size={26} weight="duotone" className="md-career-perk-ico" />
                  <h3 className="font-serif text-lg mt-3">{p.title}</h3>
                  <p className="text-sm mt-2" style={{ color: "var(--md-text-soft)" }} dangerouslySetInnerHTML={{ __html: p.body }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="md-section" data-testid="career-apply-band">
        <div className="md-container">
          <div className="md-career-apply-band">
            <div>
              <div className="md-label-eyebrow" style={{ color: "var(--md-secondary)" }}>Ready when you are</div>
              <h2 className="text-3xl md:text-4xl font-serif mt-2" style={{ color: "var(--md-bg)" }}>
                Good practitioners know good practitioners
              </h2>
              <p className="text-sm mt-3" style={{ color: "rgba(255,255,255,0.78)", maxWidth: 520 }}>
                We&apos;re always looking to connect with thoughtful clinicians, researchers, educators, and professionals who align with our mission.
              </p>
            </div>
            <Link to="/contact?source=footer" className="btn-accent md-career-apply-btn" data-testid="career-band-refer-cta">
              Refer a Colleague <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </Page>
  );
}
